import { ButtonType, OutlineTextFieldWidget } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { LocalAuthConfiguration } from '~src/authentication/models/LocalAuthConfiguration';
import { IPasswordPolicy } from '~src/authentication/models/validation/IPasswordPolicy';
import { IIdentityPool } from '~src/authentication/serverApi/types/identityPools/IIdentityPool';
import { IdentityPoolService } from '~src/authentication/services/IdentityPoolService';
import { ButtonWithSpinner } from '~src/commonInfra/components/ButtonWithSpinner';

import { AuthenticationTypeResolutionService } from '../../authentication/services/AuthenticationTypeResolutionService';
import { Formatters, Messages } from '../../codegen/Localize';
import { ServerSettingsUnauthenticated } from '../../settings/services/ServerSettingsUnauthenticated';
import { AuthenticationProviderSelector } from '../../settings/siteAuthSettings/directives/AuthenticationProviderSelector';
import { AuthenticationProviderSelectorProperties, getDefaultAuthenticationProviderSelectorProperties } from '../../settings/siteAuthSettings/model/AuthenticationProviderSelectorProperties';
import { AuthenticationProviderType, IAuthenticationProvider } from '../../settings/siteAuthSettings/services/SiteAuthenticationSettingsService';
import { AddLocalUserDialogType } from '../../userManagement/services/AddLocalUserAction';
import { EmailValidationHelper } from '../helpers/EmailValidationHelper';
import { ValidationHelpers } from '../helpers/ValidationHelpers';
import { DefaultSiteRole, SiteRoleOption, SiteRoleOptionsResource } from '../services/resources/SiteRoleOptions';
import { SiteRoles } from '../users/serverApi/types/ISiteRole';
import { CreateLocalUserOnOtherSiteForm } from './CreateLocalUserOnOtherSiteForm';
import { AddLocalUserToSiteHelperService } from './helpers/AddLocalUserToSiteHelperService';
import { CreateLocalServerUserHelperService } from './helpers/CreateLocalServerUserHelperService';
import { CreateLocalSiteUserHelperService } from './helpers/CreateLocalSiteUserHelperService';
import { ISetupLocalUserFormModelChanges, SetupLocalUserModelHelper } from './helpers/SetupLocalUserModelHelper';
import { NewLocalUserForm } from './NewLocalUserForm';
import { ISiteRoleChanges } from './SiteRolesPickerContainer';

export const AddLocalUserFormState = {
  Initial: 'initial',
  NewUser: 'newUser',
  OtherSite: 'otherSite',
  NotAvailable: 'notAvailable'
};

// Contains the model for the whole form, including all different flows.
export interface IFormModel {
  username: string;
  formState: string;
  errorText: string;
  newUserFlowModel: INewUserFlowModel;
  otherSiteFlowModel: IOtherSiteFlowModel;
  notAvailableFlowModel: INotAvailableFlowModel;
  authenticationProviderSelectorProperties: AuthenticationProviderSelectorProperties;
}

export interface AddLocalUsersDialogState {
  readonly busy: boolean;
  readonly userInput: string;
  readonly formModel: IFormModel;
  readonly siteRole: string;
  readonly siteRoleDropdownDisabled: boolean;
  readonly sitePickerMenuDisabled: boolean;
}

export interface AddLocalUsersDialogProps {
  readonly dismiss: () => void;
  readonly close: (result: any) => void;
  readonly addLocalUserDialogType: string;
  readonly availableQuota?: number;
  readonly identityPool?: IIdentityPool;
}

// Model for ux Flow #1:
// => Username is available
export interface INewUserFlowModel {
  displayName: string;
  password: string;
  confirmPassword: string;
  email: string;
  usernameInfo: string;
  identifier: string;
}

// Model for ux Flow #2:
// => Username exists on another site
export interface IOtherSiteFlowModel {
  displayName: string;
  warningText: string;
  proceedClicked: boolean;
}

// Model for ux Flow #3:
// => Username exists on current site
export interface INotAvailableFlowModel {
  displayName: string;
  errorText: string;
}

// Contains the model for the whole form, including all different flows.
export interface IFormModel {
  username: string;
  formState: string;
  errorText: string;
  newUserFlowModel: INewUserFlowModel;
  otherSiteFlowModel: IOtherSiteFlowModel;
  notAvailableFlowModel: INotAvailableFlowModel;
  authenticationProviderSelectorProperties: AuthenticationProviderSelectorProperties;
}

export class AddLocalUsersDialog extends React.Component<
  AddLocalUsersDialogProps,
  AddLocalUsersDialogState
> {
  private siteRoleOptionsResource: SiteRoleOptionsResource = SiteRoleOptionsResource.instance;
  public localAuthConfiguration: LocalAuthConfiguration = LocalAuthConfiguration.default();
  public siteRoleChanges: ISiteRoleChanges;
  public setupLocalUserModelHelper = new SetupLocalUserModelHelper();

  public createLocalSiteUserHelperService: CreateLocalSiteUserHelperService = new CreateLocalSiteUserHelperService();
  public createLocalServerUserHelperService: CreateLocalServerUserHelperService =
    new CreateLocalServerUserHelperService();
  public addLocalUserToSiteHelperService: AddLocalUserToSiteHelperService =
    new AddLocalUserToSiteHelperService();

  public constructor(props: AddLocalUsersDialogProps) {
    super(props);
    this.state = {
      busy: false,
      userInput: '',
      siteRoleDropdownDisabled: false,
      formModel: {
        username: '',
        formState: AddLocalUserFormState.Initial,
        errorText: '',
        newUserFlowModel: {
          displayName: '',
          password: '',
          confirmPassword: '',
          email: '',
          usernameInfo: '',
          identifier: ''
        },
        otherSiteFlowModel: {
          displayName: '',
          warningText: '',
          proceedClicked: false
        },
        notAvailableFlowModel: {
          displayName: '',
          errorText: ''
        },
        authenticationProviderSelectorProperties: getDefaultAuthenticationProviderSelectorProperties()
      },
      siteRole: props.availableQuota <= 0 ? SiteRoles.Unlicensed : DefaultSiteRole,
      sitePickerMenuDisabled: false
    };
    this.siteRoleChanges = {
      siteRoles: {},
      serverAdmin: undefined
    };
  }

  public async componentDidMount() {
    this.localAuthConfiguration = await LocalAuthConfiguration.fetchCurrentLocalAuthConfiguration();
  }

  public readonly importUsersCallback = () => {
    this.confirm();
  };

  public readonly userNameChangeCallback = (userInput: string) => {
    this.setState({ ...this.state, userInput }, () => this.setupLocalUserFormModel());
  };

  public readonly displayNameChangeCallback = (displayName: string) => {
    const newFormModel = { ...this.state.formModel };
    newFormModel.newUserFlowModel.displayName = displayName;
    this.setState({ formModel: newFormModel });
  };

  public readonly passwordChangeCallback = (password: string) => {
    const newFormModel = { ...this.state.formModel };
    newFormModel.newUserFlowModel.password = password;
    this.setState({ formModel: newFormModel });
  };

  public readonly emailChangeCallback = (email: string) => {
    const newFormModel = { ...this.state.formModel };
    newFormModel.newUserFlowModel.email = email;
    this.setState({ formModel: newFormModel });
  };

  public readonly identifierChangeCallback = (identifier: string) => {
    const newFormModel = { ...this.state.formModel };
    newFormModel.newUserFlowModel.identifier = identifier;
    this.setState({ formModel: newFormModel });
  };

  public readonly confirmPasswordChangeCallback = (confirmPassword: string) => {
    const newFormModel = { ...this.state.formModel };
    newFormModel.newUserFlowModel.confirmPassword = confirmPassword;
    this.setState({ formModel: newFormModel });
  };

  public readonly siteRoleChangesCallback = (siteRoleChanges: ISiteRoleChanges) => {
    this.siteRoleChanges = siteRoleChanges;
  };

  public readonly modelChangeCallback = (model: AuthenticationProviderSelectorProperties) => {
    const newFormModel = { ...this.state.formModel };
    newFormModel.authenticationProviderSelectorProperties = model;
    this.setState({ formModel: newFormModel });
  };

  public componentDidUpdate(prevProps: AddLocalUsersDialogProps, prevState: AddLocalUsersDialogState) {
    if (prevState.formModel.formState !== this.state.formModel.formState) {
      if (typeof (Event) === 'function') {
        window.dispatchEvent(new UIEvent('resize'));
      } else {
        const evt = window.document.createEvent('UIEvents');
        evt.initEvent('resize', true, false);
        window.dispatchEvent(evt);
      }
    }
  }

  public render() {
    return (
      <div className="tb-add-local-user">
        <div className="tb-dialog-title">{Messages.addLocalUser()}</div>
        <div>
          {this.renderAuthenticationProviderSelector()}
          <table className="tb-form-table">
            <tbody>
              <tr>
                <td className="tb-text-input-label-cell">
                  <label> {Messages.username_colon()} </label>
                </td>
                <td className="tb-settings-control tb-input-wide">
                  <OutlineTextFieldWidget
                    handleChange={this.userNameChangeCallback}
                    testId="username"
                    disabled={this.state.busy}
                    text={this.state.userInput}
                  />
                  {this.availableQuotaMessage()}
                </td>
              </tr>
            </tbody>
            {this.setupLocalUserForm()}
          </table>
        </div>
        {this.createErrorMessages()}
        <div className="tb-dialog-actions">
          <ButtonWithSpinner
            buttonType={ButtonType.Outline}
            onClick={this.props.dismiss}
            testId={'confirm-action-dialog-cancel'}
            children={Messages.cancel()}
            disabled={this.state.busy}
          />
          <span className="tb-lr-padded">
            <ButtonWithSpinner
              buttonType={ButtonType.Primary}
              showSpinner={this.state.busy}
              onClick={this.importUsersCallback}
              testId={'confirm-action-dialog-confirm'}
              children={this.submitButtonLabel()}
              disabled={this.isConfirmDisabled()}
            />
          </span>
        </div>
      </div>
    );
  }

  public setupLocalUserFormModel = () => {
    this.setupLocalUserModelHelper.setupLocalUserFormModel({ ...this.state.formModel }, this.state.userInput, this.props.addLocalUserDialogType, this.state.sitePickerMenuDisabled, this.props.identityPool)
      .then((setupResult: ISetupLocalUserFormModelChanges) => {
        this.setState({ formModel: setupResult.formModel, sitePickerMenuDisabled: setupResult.sitePickerMenuDisabled });
      });
  };

  private renderAuthenticationProviderSelector() {
    let currentModel = this.state.formModel.authenticationProviderSelectorProperties;
    const onProviderChange = (provider: IAuthenticationProvider) => {
      this.modelChangeCallback({ ...currentModel, authenticationProvider: provider });
    };
    const onProviderIdsChange = (ids: string[]) => {
      this.modelChangeCallback({ ...currentModel, authenticationProviderIds: ids });
    };
    const onAuthSettingChange = (authSetting: string) => {
      this.modelChangeCallback({ ...currentModel, authSetting: authSetting });
    };

    if (this.props.addLocalUserDialogType === AddLocalUserDialogType.SITE && ServerSettingsUnauthenticated.isSiteSamlEnabled()) {
      return (
        <AuthenticationProviderSelector {...this.state.formModel.authenticationProviderSelectorProperties} onProviderChange={onProviderChange} onProviderIdsChange={onProviderIdsChange} onAuthSettingChange={onAuthSettingChange} />
      );
    }
    return (null);
  }

  private setupLocalUserForm(): JSX.Element {
    if (this.state.formModel.formState === AddLocalUserFormState.NewUser) {
      return (
        <NewLocalUserForm
          availableQuota={this.props.availableQuota}
          busy={this.state.busy}
          formModel={this.state.formModel}
          displayNameChangeHandler={this.displayNameChangeCallback}
          emailChangeHandler={this.emailChangeCallback}
          passwordChangeHandler={this.passwordChangeCallback}
          confirmPasswordChangeHandler={this.confirmPasswordChangeCallback}
          siteRoleChangesHandler={this.siteRoleChangesCallback}
          getSiteRoleOptionBySiteRoleHandler={this.getSiteRoleOptionBySiteRoleCallback}
          onOptionSelectedHandler={this.onOptionSelectedCallback}
          identifierChangeHandler={this.identifierChangeCallback}
          addLocalUserDialogType={this.props.addLocalUserDialogType}
          siteRole={this.state.siteRole}
          sitePickerMenuDisabled={this.state.sitePickerMenuDisabled}
          shouldShowPasswordField={this.shouldShowPasswordField()}
          shouldShowIdentifierField={this.shouldShowIdentifierField()} />
      );
    } else if (this.state.formModel.formState === AddLocalUserFormState.OtherSite) {
      return (
        <CreateLocalUserOnOtherSiteForm
          availableQuota={this.props.availableQuota}
          busy={this.state.busy}
          formModel={this.state.formModel}
          onProceedClickedHandler={this.onProceedClicked}
          getSiteRoleOptionBySiteRoleHandler={this.getSiteRoleOptionBySiteRoleCallback}
          onOptionSelectedHandler={this.onOptionSelectedCallback}
          addLocalUserDialogType={this.props.addLocalUserDialogType}
          siteRole={this.state.siteRole}
          sitePickerMenuDisabled={this.state.sitePickerMenuDisabled} />
      );
    } else if (this.state.formModel.formState === AddLocalUserFormState.NotAvailable) {
      return this.createNotAvailableError();
    }
    return (null);
  }

  private createNotAvailableError(): JSX.Element {
    return (<tbody className="tb-notAvailableFlow-table">
      <tr>
        <td className="tb-text-input-label-cell">
          <label>{Messages.displayName_colon()}</label>
        </td>
        <td className="tb-immutable-text-cell">
          <label>{this.state.formModel.notAvailableFlowModel.displayName}</label>
        </td>
      </tr>
      <tr>
        <td />
        <td className="tb-error-cell">
          <div className="tb-error-text">{this.state.formModel.notAvailableFlowModel.errorText}</div>
          <div className="tb-error-text">{Messages.tryAnotherUsername()}</div>
        </td>
      </tr>
    </tbody>);
  }

  private readonly onProceedClicked = () => {
    const formModel = { ...this.state.formModel };
    formModel.otherSiteFlowModel.proceedClicked = true;
    this.setState({ sitePickerMenuDisabled: false, formModel });
  };

  private readonly getSiteRoleOptionBySiteRoleCallback = (siteRole: string): SiteRoleOption => {
    return this.siteRoleOptionsResource.getSiteRoleOptionBySiteRole(siteRole);
  };

  private readonly onOptionSelectedCallback = (siteRoleOption: SiteRoleOption): void => {
    this.setState({ siteRole: siteRoleOption.role });
  };

  private createErrorMessages() {
    const passwordCandidate = this.state.formModel.newUserFlowModel.password;
    return (<div>
      <div className="tb-error-text">{this.state.formModel.errorText}</div>
      {this.getUsernameError()}
      {this.getPasswordMatchError()}
      {passwordCandidate && ValidationHelpers.createPasswordPolicyErrorMessageSectionFor(
        this.getFailedPasswordPolicies(passwordCandidate))}
    </div>);
  }

  private getFailedPasswordPolicies(passwordCandidate: string): IPasswordPolicy[] {
    return this.localAuthConfiguration.performPasswordValidation(passwordCandidate);
  }

  private getUsernameError(): JSX.Element {
    if (this.hasInvalidUsername()) {
      return (<div className="tb-error-text usernameError">
        {Messages.usernameInvalid()}
        <div>{ValidationHelpers.INVALID_USERNAME_CHARS_STR}</div>
      </div>);
    }
    return (null);
  }

  public hasInvalidUsername(): boolean {
    return ValidationHelpers.hasInvalidUsername(this.state.userInput);
  }

  private getPasswordMatchError(): JSX.Element {
    if (this.hasNonMatchingPasswords()) {
      return (<div className="tb-error-text passwordMatchingError">{Messages.passwordsMustMatch()}</div>);
    }
    return (null);
  }

  public hasNonMatchingPasswords(): boolean {
    return ValidationHelpers.hasNonMatchingPasswords(
      this.state.formModel.newUserFlowModel.password,
      this.state.formModel.newUserFlowModel.confirmPassword);
  }

  public submitButtonLabel() {
    if (!ServerSettingsUnauthenticated.isTableauIdSupported() && this.state.formModel.otherSiteFlowModel.proceedClicked) {
      return Messages.addUserToSite();
    }
    return Messages.addUserDialogSubmit();
  }

  private availableQuotaMessage(): JSX.Element {
    if (_.isNumber(this.props.availableQuota)) {
      return (
        <div className={'tb-available-quota tb-row'}>
          {Messages.availableQuota({ NUM_USERS: this.props.availableQuota, NUM_USERS_LOC: Formatters.formatCount(this.props.availableQuota) })}
        </div>
      );
    }
    return (null);
  }

  private shouldShowPasswordField(): boolean {
    const authenticationProvider = this.state.formModel.authenticationProviderSelectorProperties.authenticationProvider;
    const isAuthExternal = AuthenticationTypeResolutionService.isAuthenticationExternal();
    const isDefaultIdentityPool = this.props.identityPool == null || this.props.identityPool?.id === IdentityPoolService.DEFAULT_POOL_ID;

    return authenticationProvider.type === AuthenticationProviderType.default && !isAuthExternal && isDefaultIdentityPool;
  }

  private shouldShowIdentifierField(): boolean {
    /* Login with local username/password would fail if username and identifier are different.
       Do not show the identifier field for user to input so that we use same value for username and identifier. */
    const isDefaultIdentityPool = this.props.identityPool == null || this.props.identityPool?.id === IdentityPoolService.DEFAULT_POOL_ID;
    const isAuthLocal = AuthenticationTypeResolutionService.isAuthenticationLocal();

    return !(isDefaultIdentityPool && isAuthLocal);
  }

  public isLocalUserInputsValid(): boolean {
    const passwordCandidate = this.state.formModel.newUserFlowModel.password;
    return (!this.hasInvalidEmail() &&
      ValidationHelpers.isLocalUserInputsValid(
        this.state.formModel.username,
        this.state.formModel.newUserFlowModel.displayName,
        this.state.formModel.newUserFlowModel.password,
        this.state.formModel.newUserFlowModel.confirmPassword,
        this.shouldShowPasswordField(),
        !this.getFailedPasswordPolicies(passwordCandidate).length));
  }

  public hasInvalidEmail() {
    if (this.state.formModel.newUserFlowModel.email) {
      return !EmailValidationHelper.isValidEmail(this.state.formModel.newUserFlowModel.email);
    }
    return false;
  }

  public isConfirmDisabled(): boolean {
    switch (this.state.formModel.formState) {

      case AddLocalUserFormState.Initial:
      case AddLocalUserFormState.NewUser:
        return !this.isLocalUserInputsValid();

      case AddLocalUserFormState.OtherSite:
        return !this.state.formModel.otherSiteFlowModel.proceedClicked;

      case AddLocalUserFormState.NotAvailable:
        return true;
    }

    return false;
  }

  private setErrorTextCallback = (errorMsg: string) => {
    const formModel = { ...this.state.formModel };
    formModel.errorText = errorMsg;
    this.setState({ formModel });
  };

  private setBusyState = (busy: boolean) => {
    this.setState({ busy });
  };

  public confirm() {
    this.setState({ busy: true });
    const username = this.state.formModel.username;
    const authenticationProvider = this.state.formModel.authenticationProviderSelectorProperties.authenticationProvider;
    switch (this.state.formModel.formState) {

      case AddLocalUserFormState.NewUser:
        if (this.props.addLocalUserDialogType === AddLocalUserDialogType.SITE) {
          this.createLocalSiteUserHelperService.createLocalSiteUser(username, this.state.formModel.newUserFlowModel, this.state.siteRole, authenticationProvider, this.props, this.setErrorTextCallback, this.setBusyState);
        } else if (this.props.addLocalUserDialogType === AddLocalUserDialogType.SERVER) {
          this.createLocalServerUserHelperService.createLocalServerUser(username, this.state.formModel.newUserFlowModel, authenticationProvider, this.siteRoleChanges, this.props, this.setBusyState);
        }
        break;

      case AddLocalUserFormState.OtherSite:
        this.addLocalUserToSiteHelperService.addUserToSite(username, this.state.siteRole, this.props, this.setErrorTextCallback, this.setBusyState);
        break;
    }
  }
}
